import { FormEvent, useMemo, useState } from "react";

import { TaskConstants } from "../../../../constants/workflow/TaskConstants";
import { RadioButtonGroupData, RadioButtonGroupKey } from "../../../../models";
import { useUpdateEffect } from "../../../../utils";

interface UseNewTaskModalProps {
  show: boolean;
  // Each task type is created through a different API path with its own permission, so they are gated
  // separately — an unavailable type is not offered at all rather than rejected after the overlay.
  canCreateWorkflowTask: boolean;
  canCreateGeneralTask: boolean;
  onConfirm: (isGeneralTask: boolean) => void;
}

interface UseNewTaskModalReturnData {
  taskType: RadioButtonGroupKey;
  setTaskType: (value: RadioButtonGroupKey) => void;
  taskTypeOptions: RadioButtonGroupData;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
}

export const useNewTaskModal = ({
  show,
  canCreateWorkflowTask,
  canCreateGeneralTask,
  onConfirm,
}: UseNewTaskModalProps): UseNewTaskModalReturnData => {
  const taskTypeOptions = useMemo(
    () =>
      TaskConstants.TASK_TYPE_RADIO_DATA.filter(
        (option) =>
          (option.key === TaskConstants.TASK_TYPE_WORKFLOW && canCreateWorkflowTask) ||
          (option.key === TaskConstants.TASK_TYPE_GENERAL && canCreateGeneralTask)
      ),
    [canCreateWorkflowTask, canCreateGeneralTask]
  );

  // Never default to a type the user cannot create.
  const defaultTaskType = canCreateWorkflowTask ? TaskConstants.TASK_TYPE_WORKFLOW : TaskConstants.TASK_TYPE_GENERAL;

  const [taskType, setTaskType] = useState<RadioButtonGroupKey>(defaultTaskType);

  // The modal stays mounted between openings, so reset to the default selection each time it is reopened.
  useUpdateEffect(() => {
    if (show) setTaskType(defaultTaskType);
  }, [show, defaultTaskType]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    onConfirm(taskType === TaskConstants.TASK_TYPE_GENERAL);
  };

  return {
    taskType,
    setTaskType,
    taskTypeOptions,
    handleSubmit,
  };
};
